import { useState } from 'react';
import { X, Zap, Check, Mail } from 'lucide-react';
import { pricingPacks, type PricingPack } from '../../data/pricing';

interface PurchaseModalProps {
  onClose: () => void;
  reportsRemaining?: number | null;
}

export default function PurchaseModal({ onClose, reportsRemaining }: PurchaseModalProps) {
  const [selected, setSelected] = useState<PricingPack | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 dark:bg-black/70"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-surface-900 rounded-xl shadow-xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-surface-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Get More Reports
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Current balance */}
          {reportsRemaining !== undefined && (
            <div className="flex items-center gap-2 mb-6 px-3 py-2 bg-gray-50 dark:bg-surface-800 rounded-lg">
              <Zap size={16} className="text-teal-600 dark:text-teal-400" />
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Current balance:{' '}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {reportsRemaining === null ? 'Unlimited' : `${reportsRemaining} reports`}
                </span>
              </span>
            </div>
          )}

          {showConfirmation ? (
            /* Confirmation message */
            <div className="text-center py-8">
              <div className="w-14 h-14 bg-teal-50 dark:bg-teal-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail size={28} className="text-teal-600 dark:text-teal-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Purchase: {selected?.name} Pack
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                {selected?.reports.toLocaleString()} reports for ${selected?.price}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                To complete your purchase, contact us at
              </p>
              <a
                href="mailto:info@axiomdata.com.au"
                className="text-teal-600 dark:text-teal-400 font-medium hover:underline"
              >
                info@axiomdata.com.au
              </a>
              <button
                onClick={() => { setShowConfirmation(false); setSelected(null); }}
                className="mt-6 block mx-auto px-4 py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Back to packs
              </button>
            </div>
          ) : (
            <>
              {/* Pack grid */}
              <div className="grid sm:grid-cols-2 gap-4">
                {pricingPacks.map((pack) => (
                  <button
                    key={pack.name}
                    onClick={() => setSelected(pack)}
                    className={`relative text-left p-4 rounded-xl border-2 transition-all ${
                      selected?.name === pack.name
                        ? 'border-teal-500 bg-teal-50/50 dark:bg-teal-900/10'
                        : 'border-gray-200 dark:border-surface-700 hover:border-gray-300 dark:hover:border-surface-600'
                    }`}
                  >
                    {pack.popular && (
                      <span className="absolute -top-2.5 left-4 px-2 py-0.5 bg-teal-600 text-white text-[10px] font-semibold rounded-full">
                        Most Popular
                      </span>
                    )}
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {pack.name}
                      </h3>
                      {selected?.name === pack.name && (
                        <Check size={18} className="text-teal-600 dark:text-teal-400" />
                      )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      {pack.reports.toLocaleString()} reports
                    </p>
                    <div className="mt-2">
                      <span className="text-2xl font-bold text-gray-900 dark:text-white">
                        ${pack.price}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">
                        (${pack.perReport}/report)
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Purchase button */}
              <div className="mt-6">
                <button
                  disabled={!selected}
                  onClick={() => setShowConfirmation(true)}
                  className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    selected
                      ? 'bg-teal-600 hover:bg-teal-700 text-white'
                      : 'bg-gray-100 dark:bg-surface-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  }`}
                >
                  {selected
                    ? `Purchase ${selected.name} — $${selected.price}`
                    : 'Select a pack'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
